<template>
  <div class="survey-multi-form container">
    <b-btn variant="primary" class="float-right mb-3" @click="save">
      Save
    </b-btn>

    <h1>Edit Surveys</h1>

    <b-row>
      <b-col class="table-wrapper">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th colspan="3"></th>
              <th class="user text-center" v-for="survey in surveys" :key="survey.id">
                <span :title="survey.user.email + ' ' + survey.created">
                  #{{ survey.id }}
                </span>
              </th>
            </tr>
          </thead>
          <tr v-for="v in variables">
            <template v-for="q in [getVariableQuestion(v)]">
              <th
                v-if="isFirstVariableForQuestion(v)"
                :rowspan="q.variables.length"
              >
                <span class="question-id">Q{{ q.id }}</span>
              </th>
              <th
                v-if="isFirstVariableForQuestion(v)"
                :rowspan="q.variables.length"
              >
                {{ q.label }}
              </th>
              <th>
                {{ getVariableLabel(v) }}
              </th>
              <td class="text-center input-cell" v-for="survey in surveys">
                <template v-for="r in [getResponse(survey, v)]">
                  <b-form-radio
                    v-if="getVariableQuestion(v).type == 'SELECT_ONE'"
                    v-model="r.value"
                    :value="1"
                    @change="selectOneRadioChanged(survey, v)"
                  >
                  </b-form-radio>

                  <b-form-checkbox
                    v-else-if="getVariableQuestion(v).type == 'YES_NO'"
                    v-model="r.value"
                    :value="1"
                    :unchecked-value="0"
                  >
                  </b-form-checkbox>

                  <b-form-checkbox
                    v-else-if="getVariableQuestion(v).type == 'SELECT_ANY'"
                    v-model="r.value"
                    :value="1"
                    :unchecked-value="0"
                  >
                  </b-form-checkbox>

                  <b-form-select
                    v-else-if="getVariableQuestion(v).type == 'FIND_STATE'"
                    v-model="r.value"
                    :options="getStateOptions()"
                  >
                  </b-form-select>

                  <b-form-select
                    v-else-if="getVariableQuestion(v).type == 'FIND_REGION'"
                    v-model="r.value"
                    :options="getRegionOptions()"
                  >
                  </b-form-select>

                  <b-form-input v-else type="text" size="sm" v-model="r.value">
                  </b-form-input>
                </template>
              </td>
            </template>
          </tr>
        </table>
      </b-col>
    </b-row>
  </div>
</template>

<!-- -------------------------------------------------------- -->

<script>
import Vue from 'vue';
import Api from '@/providers/Api.js';
import states from '@/providers/States.js';
import regions from '@/providers/Regions.js';
import flatten from 'lodash/flatten';
import map from 'lodash/map';

export default Vue.extend({
  name: 'surveyMultiForm',
  data() {
    return {
      questionnaire: {},
      surveys: [],
    };
  },
  computed: {
    questions() {
      if (this.questionnaire.id) {
        const qs = flatten(
          this.questionnaire.sections.map((section) => {
            return section.questionSections.map((qs) => qs.question);
          })
        );
        return qs;
      } else {
        return [];
      }
    },
    variables() {
      if (this.questions.length) {
        return flatten(
          this.questions.map((q) => {
            return q.variables;
          })
        );
      } else {
        return [];
      }
    },
    responses() {
      return flatten(
        this.surveys.map((survey) => {
          return survey.responses;
        })
      );
    },
  },
  methods: {
    getVariableQuestion(variable) {
      return this.questions.find((q) => variable.question_id == q.id);
    },
    isFirstVariableForQuestion(variable) {
      return variable.id == this.getVariableQuestion(variable).variables[0].id;
    },
    getVariableLabel(variable) {
      return variable.short_label ? variable.short_label : variable.input_label;
    },
    getStateOptions() {
      return map(states.states, (name, key) => {
        return { value: key, text: name };
      });
    },
    getRegionOptions() {
      return map(regions.regions, (name, key) => {
        return { value: key, text: name };
      });
    },
    getResponse(survey, variable) {
      if (!survey) {
        return null;
      } else {
        let response = survey.responses.find(
          (r) => r.variable_id == variable.id
        );
        // create response for question added after survey started
        if (!response) {
          response = {
            survey_id: survey.id,
            variable_id: variable.id,
            value: null,
          };
          survey.responses.push(response);
        }
        return response;
      }
    },
    selectOneRadioChanged(survey, variable) {
      // When a radio for a SELECT_ONE question is selected, set the values for the other vars to null
      // (This is different than the YES_NO case, where both radios are backed by a single
      // boolean variable. In this case each radio has a boolean variable, and we have to work around
      // the normal way of binding values to radios. Each radio is essentially behaving like a checkbox,
      // but we only allow one to be selected.)
      survey.responses.forEach((r) => {
        if (r.question_id == variable.question_id) {
          r.value = r.variable_id === variable.id ? 1 : 0;
        }
      });
    },
    save() {
      Api.updateResponses(this.responses).then(() => {
        this.$router.push({ path: '/admin/survey' });
        this.$toasted.show('Responses saved', { duration: 3000 });
      });
    },
  },
  created() {
    Api.getQuestionnaireWithQuestionsAndVariables(this, 1).then(
      (questionnaire) => {
        this.questionnaire = questionnaire;
      }
    );

    // load surveys based on URL params
    const filters = {
      id: { field: 'id', type: 'array', value: this.$route.params.surveyIds },
    };
    Api.getSurveys(this, filters).then((surveys) => {
      this.surveys = surveys;
    });
  },
});
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss" scoped>
.table-wrapper {
  width: 100%;
  overflow-x: scroll;
}

th {
  font-size: 0.9em;
}

td,
th {
  padding: 0.2rem;
}

.input-cell {
  width: 90px;
}
.question-id {
  color: #bbb;
}

.user {
  font-size: 75%;
  color: #bbb;
  line-height: 1 !important;
}

input[type='text'] {
  width: 80px;
  text-align: right;
}
select {
  width: 80px;
}
</style>
